import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { BackHeader } from '@/components/BackHeader/BackHeader'
import { LoadingReport } from '@/components/LoadingReport/LoadingReport'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useLazyGetImpersonationSearchStatusQuery,
  useStartImpersonationSearchMutation,
} from '@/store/api/Impersonation/Impersonation.query'
import { EImpersonationSearchStatus } from '@/store/api/Impersonation/Impersonation.types'

import { genericLoadingMessages } from '../Setup/Const/LoadingMessages'

const LS_KEY = 'impersonationReportId'
const POLL_INTERVAL_MS = 5000

export const ImpersonationGeneratingReport = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const hasStarted = useRef(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0)
  // The backend reports no granular progress for this search — fake a slow ramp capped at 90%
  const [progress, setProgress] = useState(5)

  const [startSearch] = useStartImpersonationSearchMutation()
  const [getSearchStatus] = useLazyGetImpersonationSearchStatusQuery()

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const startPolling = useCallback(
    (reportId: string) => {
      const checkStatus = async () => {
        try {
          const response = await getSearchStatus({ id: reportId }).unwrap()
          const status = response.info.status

          setLoadingMessageIndex(
            (prevIndex) => (prevIndex + 1) % genericLoadingMessages.length,
          )
          setProgress((prevProgress) => Math.min(prevProgress + 5, 90))

          if (status === EImpersonationSearchStatus.Complete) {
            stopInterval()
            localStorage.removeItem(LS_KEY)
            navigate(AppRoutes.impersonationReport, {
              state: { id: reportId },
            })
          } else if (status === EImpersonationSearchStatus.Error) {
            stopInterval()
            localStorage.removeItem(LS_KEY)
            toast.error(t('impersonation.generating.errorGenerating'))
            navigate(AppRoutes.dashboard)
          }
        } catch (error) {
          stopInterval()
          catchWithSentry(error)
          toast.error(t('feedback.toast.errorFetchingStatus'))
          navigate(AppRoutes.dashboard)
        }
      }

      stopInterval()
      intervalRef.current = setInterval(checkStatus, POLL_INTERVAL_MS)
    },
    [getSearchStatus, navigate, stopInterval, t],
  )

  useEffect(() => {
    if (hasStarted.current) return
    hasStarted.current = true

    const storedReportId = localStorage.getItem(LS_KEY)

    if (storedReportId) {
      // Resume polling an in-flight search (e.g. the user refreshed the page)
      startPolling(storedReportId)
      return
    }

    startSearch()
      .unwrap()
      .then((response) => {
        const reportId = response.info.id
        localStorage.setItem(LS_KEY, reportId)
        startPolling(reportId)
      })
      .catch((error) => {
        catchWithSentry(error)
        // 403 = no credits yet: usually Stripe webhook lag right after checkout
        if (error?.status === 403) {
          toast.info(t('impersonation.generating.paymentProcessing'))
        } else {
          toast.error(t('impersonation.generating.errorStarting'))
        }
        navigate(AppRoutes.dashboard)
      })

    return () => {
      stopInterval()
    }
  }, [navigate, startPolling, startSearch, stopInterval, t])

  return (
    <WeScreen useHeader={false}>
      <div className="absolute z-50 top-10 left-10">
        <BackHeader onPressBack={() => navigate(AppRoutes.dashboard)} />
      </div>
      <LoadingReport
        progress={progress}
        title={t('impersonation.generating.title')}
        description={t('impersonation.generating.description')}
        progressText={t(genericLoadingMessages[loadingMessageIndex])}
      />
    </WeScreen>
  )
}
